/**
 * Spec 082 FR-F3 — suggestion queue with type filtering + inline approve/reject.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/ui/Icon.js';
import {
  approveSuggestion,
  listSuggestions,
  rejectSuggestion,
  type RuleSuggestion,
  type SuggestionReviewStatus,
  type SuggestionType,
} from '../../api/ruleIngestion.js';
import { useToast } from '../../hooks/useToast.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { Select } from '../../components/Select.js';
import { SuggestionDiff } from './SuggestionDiff.js';
import { RowList, Row, RowFacts, RowFact } from '../../components/ui/Row.js';

const TYPE_OPTIONS: SuggestionType[] = [
  'provenance_only',
  'rubric_change',
  'evaluation_steps_change',
  'definition_refinement',
];

const STATUS_OPTIONS: SuggestionReviewStatus[] = ['pending', 'approved', 'rejected'];

export function RuleSuggestionsPage() {
  const toast = useToast();
  const [status, setStatus] = useState<SuggestionReviewStatus>('pending');
  const [type, setType] = useState<SuggestionType | ''>('');
  const [rows, setRows] = useState<RuleSuggestion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    listSuggestions({
      status,
      suggestion_type: type || undefined,
      limit: 50,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, type, reloadToken]);

  async function onApprove(id: string) {
    try {
      const res = await approveSuggestion(id);
      const summary =
        res.kind === 'version_bump'
          ? `New version ${res.rule_version_prev} → ${res.rule_version_new}`
          : 'Provenance appended';
      toast.addToast(summary, 'success');
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : 'Approve failed', 'error');
    }
  }

  async function onReject(id: string) {
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Reason (optional):') ?? undefined;
    try {
      await rejectSuggestion(id, reason || undefined);
      toast.addToast('Rejected', 'success');
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : 'Reject failed', 'error');
    }
  }

  return (
    <div data-testid="admin-rule-suggestions" className="hub-page--table">
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[
          { label: 'Observatory', to: '/admin' },
          { label: 'Rule suggestions' },
        ]}
      />
      <header>
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-git-pull-request" className="text-violet-600" />
          Rule suggestions
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Proposed modifications to existing rules, classified by change type.
        </p>
      </header>

      <div className="hub-plate grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <label>
          <span className="block text-slate-600 mb-1">Status</span>
          <Select
            data-testid="suggestions-filter-status"
            value={status}
            onChange={(v) => setStatus(v as SuggestionReviewStatus)}
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
          />
        </label>
        <label>
          <span className="block text-slate-600 mb-1">Type</span>
          <Select
            data-testid="suggestions-filter-type"
            value={type}
            onChange={(v) => setType(v as SuggestionType | '')}
            options={[
              { value: '', label: 'any' },
              ...TYPE_OPTIONS.map((t) => ({ value: t, label: t })),
            ]}
          />
        </label>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {err}
        </div>
      )}

      <div data-testid="suggestions-list">
        {loading && <div className="text-slate-500 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-slate-500 text-sm">No suggestions match the current filters.</div>
        )}
        {!loading && rows.length > 0 && (
          <RowList>
            {rows.map((s) => (
              // No suggestion has a detail page of its own — disclosure
              // shape, no `to`, for every row unconditionally (unlike
              // ProposalCard next door, this one never becomes navigable).
              // Because there is never an overlay here, neither the target-
              // rule link nor the source-post link needs .hub-row__interactive
              // — the escape hatch exists for when a stretched ::after CAN
              // appear, and for this component it never does.
              <Row
                key={s.id}
                data-testid={`suggestion-row-${s.id}`}
                title={s.suggested_change.slice(0, 300)}
                top={
                  <>
                    <span className="inline-block rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                      {s.suggestion_type}
                    </span>
                    <span className="text-xs text-slate-500">
                      similarity {s.similarity_score.toFixed(2)}
                    </span>
                  </>
                }
                actions={
                  s.review_status === 'pending' ? (
                    <>
                      <button
                        onClick={() => onApprove(s.id)}
                        data-testid={`suggestion-approve-${s.id}`}
                        className="hub-btn hub-btn--ghost"
                      >
                        <Icon name="bx-check" /> Approve
                      </button>
                      <button
                        onClick={() => onReject(s.id)}
                        data-testid={`suggestion-reject-${s.id}`}
                        className="hub-btn hub-btn--ghost"
                      >
                        <Icon name="bx-x" /> Reject
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-500">{s.review_status}</span>
                  )
                }
              >
                {/* Spec 102 — target rule visibility. */}
                {s.existing_rule_slug ? (
                  <p
                    className="text-sm text-slate-700 flex items-center gap-1.5"
                    data-testid="suggestion-target-rule"
                  >
                    <Icon name="bx-target-lock" className="text-violet-500 shrink-0" />
                    <span className="text-slate-500">Modifies:</span>
                    <Link
                      to={`/admin/rules?slug=${encodeURIComponent(s.existing_rule_slug)}`}
                      data-testid="suggestion-target-link"
                      className="font-medium text-violet-700 hover:underline"
                    >
                      <code className="text-xs bg-violet-50 px-1 py-0.5 rounded-md">
                        {s.existing_rule_slug}
                      </code>
                      {s.existing_rule_name ? <> — {s.existing_rule_name}</> : null}
                    </Link>
                    {s.existing_rule_status && s.existing_rule_status !== 'active' && (
                      <span className="text-xs text-amber-600">
                        ({s.existing_rule_status})
                      </span>
                    )}
                  </p>
                ) : (
                  <p
                    className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 flex items-center gap-1.5"
                    data-testid="suggestion-target-rule"
                  >
                    <Icon name="bx-error" className="text-amber-600 shrink-0" />
                    Target rule no longer exists — reject recommended.
                  </p>
                )}

                <RowFacts>
                  <RowFact icon="bx-link-external">
                    <a
                      href={s.source_post_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-violet-600 hover:underline break-words"
                    >
                      {s.source_post_title ?? s.source_post_url}
                    </a>
                  </RowFact>
                  {s.source_post_author && <RowFact>{s.source_post_author}</RowFact>}
                </RowFacts>
                <p className="text-xs text-slate-600 border-l-2 border-amber-300 pl-2">
                  “{s.supporting_quote.slice(0, 200)}”
                </p>

                {/* Spec 102 — before/after diff vs current rule values. Inner
                    layout (its own md:grid-cols-2) is untouched — this is a
                    grid INSIDE a row, not the list-level arrangement. */}
                <SuggestionDiff suggestion={s} />
              </Row>
            ))}
          </RowList>
        )}
      </div>
      <p className="text-xs text-slate-500">Total matching: {total}</p>
    </div>
  );
}
